import {
  MapContainer,
  TileLayer,
  Marker,
  Circle,
  useMapEvents,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import { useEffect, useState } from "react";

const markerIcon = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

/* ---------------- Map Click Handler ---------------- */

function LocationMarker({ onSelect }) {
  useMapEvents({
    click(e) {
      onSelect(e.latlng);
    },
  });
  return null;
}

/* ---------------- Map Fly Helper ---------------- */

function FlyTo({ position }) {
  const map = useMap();

  useEffect(() => {
    if (position) {
      map.flyTo(position, 15, { duration: 1 });
    }
  }, [position, map]);

  return null;
}

/* ---------------- Main Component ---------------- */

export default function OfficeLocationPicker({
  onSave,
  geoCoordinates,
  radius: initialRadius = 50,
}) {
  const [position, setPosition] = useState(geoCoordinates || null);
  const [radius, setRadius] = useState(initialRadius);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  /* -------- Address Search -------- */
  useEffect(() => {
    if (typeof initialRadius === "number") {
      setRadius(initialRadius);
    }
  }, [initialRadius]);

  const searchAddress = async (searchQuery) => {
    const queryToSearch = searchQuery || query;
    if (!queryToSearch.trim()) {
      setResults([]);
      return;
    }

    try {
      setLoading(true);

      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          queryToSearch,
        )}&limit=5`,
        {
          headers: {
            Accept: "application/json",
            "User-Agent": "Zenith-HRMS/1.0 (admin@zenith.app)",
          },
        },
      );

      if (!res.ok) {
        throw new Error("Search failed");
      }

      const data = await res.json();
      setResults(data);
    } catch (err) {
      console.error("❌ Address search failed:", err);
    } finally {
      setLoading(false);
    }
  };

  /* -------- Debounced Search on Type -------- */

  useEffect(() => {
    const timer = setTimeout(() => {
      if (query.trim().length >= 3) {
        searchAddress(query);
      } else if (query.trim().length === 0) {
        setResults([]);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [query]);

  const selectAddress = (item) => {
    const lat = Number(item.lat);
    const lon = Number(item.lon);

    setQuery(item.display_name);
    setPosition({ lat, lng: lon });
    setResults([]);
  };

  return (
    <div className="space-y-4">
      {/* -------- Search Box -------- */}
      <div className="relative z-[1000]">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              searchAddress();
            }
          }}
          placeholder="Search Area, Location, City or Postal Code"
          className="w-full rounded-md border px-3 py-2 text-sm pr-20"
        />

        {loading && (
          <p className="mt-1 text-xs text-slate-500">Searching address…</p>
        )}

        <button
          onClick={() => searchAddress(query)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-[#3194A0] font-semibold"
        >
          Search
        </button>

        {results.length > 0 && (
          <div className="absolute z-[1001] mt-1 w-full rounded-md border bg-secondary hover:bg-secondary/90 shadow-md max-h-56 overflow-auto">
            {results.map((r) => (
              <div
                key={r.place_id}
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectAddress(r);
                }}
                className="cursor-pointer px-3 py-2 text-sm"
              >
                {r.display_name}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* -------- Map -------- */}
      <MapContainer
        center={[19.076, 72.8777]}
        zoom={13}
        style={{ height: 400, width: "100%" }}
      >
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

        <LocationMarker onSelect={setPosition} />
        <FlyTo position={position} />

        {position && (
          <>
            <Marker position={position} icon={markerIcon} />
            <Circle
              center={position}
              radius={radius}
              pathOptions={{ color: "#3194A0" }}
            />
          </>
        )}
      </MapContainer>
      {position && (
        <div className="space-y-2">
          <label className="text-sm font-medium">
            Office Radius: <span className="font-semibold">{radius} m</span>
          </label>

          <input
            type="range"
            min={50}
            max={1000}
            step={10}
            value={radius}
            onChange={(e) => setRadius(Number(e.target.value))}
            className="w-full"
          />
        </div>
      )}

      {/* -------- Radius + Save -------- */}
      {position && (
        <>
          <button
            className="px-4 py-2 bg-[#3194A0] text-white rounded-md font-semibold"
            onClick={() =>
              onSave({
                latitude: position.lat,
                longitude: position.lng,
                radius: radius,
              })
            }
          >
            Save Office Location
          </button>
        </>
      )}
    </div>
  );
}
